import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';

import usePIIProtocols from './usePIIProtocols';
import PIIProtocolItem from './PIIProtocolItem';

const PIIConversionProtocolsPanel = () => {
  const { t } = useTranslation();
  const {
    protocols,
    page,
    totalPages,
    loading,
    error,
    goToPrev,
    goToNext,
    canGoPrev,
    canGoNext,
  } = usePIIProtocols();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
          }}
        >
          <HistoryIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('admin.pii.protocols.title', 'História anonymizácií')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'admin.pii.protocols.subtitle',
              'Prehľad nedávnych anonymizácií súborov.'
            )}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
        {loading && (
          <Box
            sx={{
              py: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && protocols.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t(
              'admin.pii.protocols.empty',
              'Zatiaľ nemáte žiadne protokoly anonymizácie.'
            )}
          </Typography>
        )}

        {!loading &&
          !error &&
          protocols.map((p) => <PIIProtocolItem key={p.id} protocol={p} />)}
      </Box>

      {/* Pagination footer */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="caption" color="text.secondary">
          {t('admin.pii.protocols.page', 'Strana {{page}} z {{total}}', {
            page,
            total: totalPages,
          })}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={goToPrev}
            disabled={!canGoPrev || loading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('common.prev', 'Predchádzajúca')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={goToNext}
            disabled={!canGoNext || loading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('common.next', 'Ďalšia')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default PIIConversionProtocolsPanel;